import { apiClient } from '@/lib/api-client';
import { withSilentErrorToast } from '@/lib/api-service';
import { API_ENDPOINTS } from '@/config/api-endpoints';

export interface LoginPayload {
  email: string;
  password: string;
  twoFactorCode?: string;
  isTwoFactorEnabled?: boolean;
  capToken?: string;
}

export interface User {
  user_id: string;
  role: 'admin' | 'reseller' | 'customer' | 'buyer_panel' | 'vendor_panel';
  display_name: string;
  reseller_id?: string;
  customer_id?: string;
  active_org_id?: string
  readonly?: boolean
  timezone?: string
  allow_tfn?: boolean;
  allow_voip?: boolean;
  allow_forward_non_tfn?: boolean;
  whitelabel_enabled?: boolean;
  brand_name?: string;
  brand_logo_url?: string;
  signup_ref_code?: string;
  primary_domain?: string;
  memberships?: {
    organizationId: string;
    role: string;
    organization: { id: string; name: string };
  }[];
}

export interface LoginResponse {
  token: string;
  me?: User;
}

export interface TwoFactorGenerateResponse {
  qrCodeUrl: string;
  manualEntryCode: string;
}

export interface TwoFactorStatus {
  isEnabled: boolean;
  isSetup: boolean;
  email?: string;
}

export const authService = {
  login: (data: LoginPayload): Promise<LoginResponse> => {
    return apiClient.post(
      API_ENDPOINTS.AUTH.LOGIN,
      {
        email: data.email,
        password: data.password,
        totp_code: data.twoFactorCode ?? '',
        cap_token: data.capToken ?? '',
      },
      withSilentErrorToast,
    );
  },

  register: (data: LoginPayload): Promise<LoginResponse> => {
    return apiClient.post(API_ENDPOINTS.AUTH.REGISTER, {
      email: data.email,
      password: data.password,
      totp_enabled: data.isTwoFactorEnabled ?? false,
    });
  },

  generateTwoFactor: (): Promise<TwoFactorGenerateResponse> => {
    // With real guards, no body needed if token is present
    return apiClient.post(API_ENDPOINTS.AUTH.TWO_FACTOR.GENERATE); 
  },

  enableTwoFactor: (twoFactorCode: string): Promise<{ success: boolean }> => {
    return apiClient.post(API_ENDPOINTS.AUTH.TWO_FACTOR.ENABLE, { 
        code: twoFactorCode, 
    });
  },

  getTwoFactorStatus: async (): Promise<TwoFactorStatus> => {
    const response = await apiClient.get<{ email?: string; totp_enabled?: boolean }>(
      API_ENDPOINTS.AUTH.TWO_FACTOR.STATUS
    );
    const data = response as unknown as { email?: string; totp_enabled?: boolean };

    return {
      isEnabled: !!data.totp_enabled,
      // Current backend exposes enabled state only; setup is treated as enabled.
      isSetup: !!data.totp_enabled,
      email: data.email,
    };
  },

  disableTwoFactor: (data?: { password?: string; code?: string }): Promise<{ success: boolean }> => {
    return apiClient.post(API_ENDPOINTS.AUTH.TWO_FACTOR.DISABLE, {
      password: data?.password ?? '',
      code: data?.code ?? '',
    });
  },

  getCurrentUser: (): Promise<User> => {
    return apiClient.get(API_ENDPOINTS.AUTH.ME);
  },

  forgotPassword: (
    email: string,
    capToken?: string,
  ): Promise<{ message?: string }> => {
    return apiClient.post(
      API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
      { email, cap_token: capToken ?? '' },
      withSilentErrorToast,
    );
  },

  resetPassword: (data: {
    token: string
    new_password: string
    capToken?: string
  }): Promise<{ message?: string }> => {
    return apiClient.post(
      API_ENDPOINTS.AUTH.RESET_PASSWORD,
      {
        token: data.token,
        new_password: data.new_password,
        cap_token: data.capToken ?? '',
      },
      withSilentErrorToast,
    );
  },

  requestTwoFactorDisable: (email: string): Promise<{ message: string }> => {
    return apiClient.post(API_ENDPOINTS.AUTH.TWO_FACTOR.RESET_REQUEST, { email });
  },

  disableTwoFactorWithToken: (token: string): Promise<{ message: string }> => {
    return apiClient.post(API_ENDPOINTS.AUTH.TWO_FACTOR.RESET_CONFIRM, { token });
  },
};
